// Slice 6: the dedicated "What changed" view. Compares the current audit against a prior one
// (default: most-recent complete) and lays the deltas out template-first, bucketed by transition
// (Worsened / New / Resolved / Improved / Removed / Still open). 'unchanged' is intentionally
// hidden — the point of this view is what moved.

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import { getAuditDiff, listAudits } from '../api';
import { formatAuditLabel } from '../utils/auditTitle';
import { useAuditBasePath } from '../utils/useAuditBasePath';
import { ALL_TEMPLATES, TemplateTabs } from '../components/TemplateTabs';
import { PlatformIcon } from '../components/PlatformIcon';
import {
  PLATFORM_LABEL,
  sectionRank,
  type AuditDiffEntry,
  type DiffTransition,
  type Severity,
  type Template
} from '../types';

const SEVERITY_LABEL: Record<Severity, string> = {
  large: 'Large',
  medium: 'Medium',
  small: 'Small',
  no_issue: 'Clean',
  na: 'N/A'
};

function sevLabel(s: Severity | null): string {
  return s ? SEVERITY_LABEL[s] : '—';
}

// Deep link from a diff row to the full finding in the main audit view. Removed findings live in
// the PRIOR audit (they're gone from the current one), so they link there; everything else links
// to the current audit. The query params drive AuditDetail's template/section + scroll-to-finding.
function findingHref(
  base: string,
  entry: AuditDiffEntry,
  currentId: string,
  priorId: string
): string {
  const targetAudit = entry.transition === 'removed' ? priorId : currentId;
  const qs = new URLSearchParams({
    template: entry.template,
    section: entry.section,
    cid: entry.cid,
    checkId: entry.checkId
  });
  return `${base}/${targetAudit}?${qs.toString()}`;
}

// Display order + styling per bucket. Most-actionable first; 'unchanged' omitted on purpose.
const BUCKETS: { key: DiffTransition; label: string; tone: string; glyph: string }[] = [
  { key: 'worsened', label: 'Worsened', tone: 'worse', glyph: '↑' },
  { key: 'new', label: 'New', tone: 'new', glyph: '⚠' },
  { key: 'resolved', label: 'Resolved', tone: 'better', glyph: '✓' },
  { key: 'improved', label: 'Improved', tone: 'better', glyph: '↓' },
  { key: 'removed', label: 'Removed', tone: 'lateral', glyph: '−' },
  { key: 'still_open', label: 'Still open', tone: 'neutral', glyph: '·' }
];

export const AuditDiffView = () => {
  const { auditId } = useParams<{ auditId: string }>();
  const base = useAuditBasePath();
  const [priorId, setPriorId] = useState<string | undefined>(undefined);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>('search');

  const diffQuery = useQuery({
    queryKey: ['auditDiff', auditId, priorId ?? 'auto'],
    queryFn: () => getAuditDiff(auditId!, priorId),
    enabled: Boolean(auditId)
  });
  // Audit list powers the prior-audit selector + CID-name lookup (runs carry their cids).
  const auditsQuery = useQuery({ queryKey: ['audits'], queryFn: listAudits });

  const { data } = diffQuery;

  const cidNameByCid = useMemo(() => {
    const run = auditsQuery.data?.find(a => a.id === auditId);
    return new Map((run?.cids ?? []).map(c => [c.cid, c.name]));
  }, [auditsQuery.data, auditId]);

  // Prior-audit options: complete runs older than the current one.
  const priorOptions = useMemo(() => {
    if (!auditsQuery.data || !data) return [];
    return auditsQuery.data
      .filter(
        a => a.status === 'complete' && a.id !== auditId && a.triggeredAt < data.current.triggeredAt
      )
      .sort((a, b) => (a.triggeredAt < b.triggeredAt ? 1 : -1));
  }, [auditsQuery.data, data, auditId]);

  // Which templates have any delta entry → which tabs to enable. Count distinct cids per template.
  const accountCountByTemplate = useMemo(() => {
    const out: Record<Template, Set<string>> = {
      search: new Set(),
      shopping: new Set(),
      pmax: new Set()
    };
    for (const e of data?.entries ?? []) {
      if (e.transition === 'unchanged') continue;
      out[e.template]?.add(e.cid);
    }
    return {
      search: out.search.size,
      shopping: out.shopping.size,
      pmax: out.pmax.size
    };
  }, [data?.entries]);

  // Snap the selected tab to the first template that actually has deltas.
  const firstWithDeltas = ALL_TEMPLATES.find(t => accountCountByTemplate[t] > 0) ?? 'search';
  const effectiveTemplate =
    accountCountByTemplate[selectedTemplate] > 0 ? selectedTemplate : firstWithDeltas;

  if (!auditId) return null;
  if (diffQuery.isLoading) return <div className='audit-loading'>Loading diff…</div>;
  if (diffQuery.isError || !data)
    return <div className='audit-error'>Failed to load the diff.</div>;

  const backLink = `${base}/${auditId}`;

  if (!data.prior) {
    return (
      <div className='audit-diff'>
        <DiffHeader
          currentTriggeredAt={data.current.triggeredAt}
          priorTriggeredAt={null}
          priorOptions={[]}
          priorId={priorId}
          onPriorChange={setPriorId}
          backLink={backLink}
        />
        <div className='audit-detail__placeholder'>
          <div className='audit-detail__placeholder-title'>No prior audit to compare against</div>
          <div className='audit-detail__placeholder-hint'>
            This is the first completed audit for Away. Run another audit later and this view will
            show what changed since this one.
          </div>
        </div>
      </div>
    );
  }

  const deltaEntries = data.entries.filter(
    e => e.template === effectiveTemplate && e.transition !== 'unchanged'
  );
  const byBucket = new Map<DiffTransition, AuditDiffEntry[]>();
  for (const e of deltaEntries) {
    const arr = byBucket.get(e.transition) ?? [];
    arr.push(e);
    byBucket.set(e.transition, arr);
  }
  for (const arr of byBucket.values()) {
    arr.sort((a, b) => {
      const r = sectionRank(a.section) - sectionRank(b.section);
      if (r !== 0) return r;
      if (a.templateRow !== b.templateRow) return a.templateRow.localeCompare(b.templateRow);
      return (cidNameByCid.get(a.cid) ?? a.cid).localeCompare(cidNameByCid.get(b.cid) ?? b.cid);
    });
  }
  const totalDeltas = data.entries.filter(e => e.transition !== 'unchanged').length;

  return (
    <div className='audit-diff'>
      <DiffHeader
        currentTriggeredAt={data.current.triggeredAt}
        priorTriggeredAt={data.prior.triggeredAt}
        priorOptions={priorOptions.map(a => ({ id: a.id, triggeredAt: a.triggeredAt }))}
        priorId={priorId}
        onPriorChange={setPriorId}
        backLink={backLink}
      />

      <TemplateTabs
        selected={effectiveTemplate}
        activeAccountCountByTemplate={accountCountByTemplate}
        onChange={setSelectedTemplate}
      />

      {deltaEntries.length === 0 ? (
        <div className='audit-detail__placeholder'>
          <div className='audit-detail__placeholder-title'>
            {totalDeltas === 0 ? 'Nothing changed' : 'No changes in this template'}
          </div>
          <div className='audit-detail__placeholder-hint'>
            {totalDeltas === 0
              ? 'Every finding holds the same state as the prior audit.'
              : 'Other templates have changes — switch tabs above.'}
          </div>
        </div>
      ) : (
        <div className='audit-diff__buckets'>
          {BUCKETS.map(b => {
            const entries = byBucket.get(b.key);
            if (!entries || entries.length === 0) return null;
            return (
              <section key={b.key} className={`diff-bucket diff-bucket--${b.tone}`}>
                <h3 className='diff-bucket__header'>
                  <span className='diff-bucket__glyph' aria-hidden>
                    {b.glyph}
                  </span>
                  {b.label}
                  <span className='diff-bucket__count'>{entries.length}</span>
                </h3>
                <ul className='diff-bucket__rows'>
                  {entries.map(e => (
                    <DiffRow
                      key={`${e.cid}|${e.checkId}`}
                      entry={e}
                      cidName={cidNameByCid.get(e.cid) ?? e.cid}
                      href={findingHref(base, e, auditId, data.prior!.id)}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DiffHeader = ({
  currentTriggeredAt,
  priorTriggeredAt,
  priorOptions,
  priorId,
  onPriorChange,
  backLink
}: {
  currentTriggeredAt: string;
  priorTriggeredAt: string | null;
  priorOptions: { id: string; triggeredAt: string }[];
  priorId: string | undefined;
  onPriorChange: (id: string | undefined) => void;
  backLink: string;
}) => (
  <header className='audit-diff__header'>
    <Link to={backLink} className='audit-diff__back'>
      ← Back to audit
    </Link>
    <div className='audit-diff__title'>
      What changed:{' '}
      <span className='audit-diff__from'>
        {priorTriggeredAt ? formatAuditLabel(priorTriggeredAt) : '—'}
      </span>{' '}
      → <span className='audit-diff__to'>{formatAuditLabel(currentTriggeredAt)}</span>
    </div>
    {priorOptions.length > 0 && (
      <label className='audit-diff__prior-select'>
        <span>Compare against</span>
        <select value={priorId ?? ''} onChange={ev => onPriorChange(ev.target.value || undefined)}>
          <option value=''>Most recent prior (auto)</option>
          {priorOptions.map(o => (
            <option key={o.id} value={o.id}>
              {formatAuditLabel(o.triggeredAt)}
            </option>
          ))}
        </select>
      </label>
    )}
  </header>
);

const DiffRow = ({
  entry,
  cidName,
  href
}: {
  entry: AuditDiffEntry;
  cidName: string;
  href: string;
}) => (
  <li>
    <Link
      to={href}
      className={classNames('diff-row', { 'diff-row--avail': entry.availabilityChanged })}
      title='Open this finding in the audit'
    >
      <span className={`platform-chip platform-chip--${entry.platform}`}>
        <PlatformIcon platform={entry.platform} size={12} />
        {PLATFORM_LABEL[entry.platform]}
      </span>
      <span className='diff-row__cid'>{cidName}</span>
      <span className='diff-row__row'>{entry.templateRow}</span>
      <span className='diff-row__section'>{entry.section}</span>
      <span className='diff-row__transition'>{transitionDetail(entry)}</span>
      <span className='diff-row__chevron' aria-hidden>
        ›
      </span>
    </Link>
  </li>
);

function transitionDetail(e: AuditDiffEntry): string {
  switch (e.transition) {
    case 'worsened':
    case 'improved':
      return `${sevLabel(e.priorSeverity)} → ${sevLabel(e.currentSeverity)}`;
    case 'resolved':
      return `was ${sevLabel(e.priorSeverity)} → Clean`;
    case 'new':
      return `new · ${sevLabel(e.currentSeverity)}`;
    case 'removed':
      return `was ${sevLabel(e.priorSeverity)} · check no longer runs`;
    case 'still_open':
      return `${sevLabel(e.currentSeverity)}${e.availabilityChanged ? ' · availability changed' : ''}`;
    default:
      return '';
  }
}
